import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { notification } from 'antd';
import type { RootState } from '../store/store';
import {
  FINANCE_ENTITIES,
  financeStatusLabel,
  openFinanceStream,
  type FinanceEntity,
  type FinanceEvent,
} from '../sse/financeStream';

/** `id` ausente = invalidar toda la entidad (resync tras reconexión). */
export type FinanceChange = { entity: FinanceEntity; id?: string };

const ENTITY_NOUN: Record<FinanceEntity, string> = {
  'partner-invoice': 'Factura',
  'partner-payout': 'Cobro',
  payout: 'Pago',
};

// Una operación suele emitir dos eventos (factura + pago): se agrupan en una sola tanda.
const FLUSH_MS = 400;
const MAX_INDIVIDUAL_TOASTS = 3;

const showToasts = (events: FinanceEvent[]) => {
  if (events.length > MAX_INDIVIDUAL_TOASTS) {
    notification.info({
      message: 'Finanzas actualizadas',
      description: `${events.length} cambios en facturas, cobros o pagos.`,
      placement: 'topRight',
    });
    return;
  }
  for (const { name, entity, data } of events) {
    notification.info({
      message: `${ENTITY_NOUN[entity]} ${data.number}`,
      description: `${name.endsWith('.created') ? 'Nuevo' : 'Actualizado'} · ${financeStatusLabel(entity, data.status)}`,
      placement: 'topRight',
    });
  }
};

/**
 * Abre un único stream de finanzas mientras haya sesión y `enabled`.
 * La app decide qué tags invalidar con `onInvalidate`.
 */
export const useFinanceStream = ({
  enabled,
  partnerId,
  onInvalidate,
}: {
  enabled: boolean;
  partnerId?: string;
  onInvalidate: (changes: FinanceChange[]) => void;
}) => {
  const dispatch = useDispatch();
  const token = useSelector((state: RootState) => state.auth.token);
  const onInvalidateRef = useRef(onInvalidate);
  onInvalidateRef.current = onInvalidate;

  useEffect(() => {
    if (!enabled || !token) return;

    let changes: FinanceChange[] = [];
    let toasts: FinanceEvent[] = [];
    let flushTimer: ReturnType<typeof setTimeout> | undefined;

    const flush = () => {
      flushTimer = undefined;
      if (changes.length) onInvalidateRef.current(changes);
      if (toasts.length) showToasts(toasts);
      changes = [];
      toasts = [];
    };
    const queue = () => {
      if (!flushTimer) flushTimer = setTimeout(flush, FLUSH_MS);
    };

    const close = openFinanceStream({
      getToken: () => token,
      partnerId,
      onEvent: (event) => {
        changes.push({ entity: event.entity, id: event.data.id });
        // Recálculos de borrador (factura en 1) son ruido: refetch sí, toast no.
        const isDraftRecalc = event.entity === 'partner-invoice' && event.data.status === 1;
        if (event.isNew && !isDraftRecalc) toasts.push(event);
        queue();
      },
      onResync: () => {
        changes.push(...FINANCE_ENTITIES.map((entity) => ({ entity })));
        queue();
      },
      onAuthLost: () => dispatch({ type: 'auth/logout' }),
    });

    return () => {
      close();
      clearTimeout(flushTimer);
    };
  }, [enabled, token, partnerId, dispatch]);
};
